import { useState, useCallback } from 'react';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';

var SAMPLE = '{"name":"Jane Smith","age":28,"skills":["Writing","Design","Marketing"],"address":{"city":"Portland","state":"OR"},"active":true}';

function JsonFormatter() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [indent, setIndent] = useState(2);
  const [copied, setCopied] = useState(false);

  var prettify = useCallback(function () {
    if (!input.trim()) { setError('Please paste some JSON first.'); setOutput(''); return; }
    try { setOutput(JSON.stringify(JSON.parse(input), null, indent)); setError(''); }
    catch (e) { setError('Invalid JSON: ' + e.message); setOutput(''); }
  }, [input, indent]);

  var minify = useCallback(function () {
    if (!input.trim()) { setError('Please paste some JSON first.'); setOutput(''); return; }
    try { setOutput(JSON.stringify(JSON.parse(input))); setError(''); }
    catch (e) { setError('Invalid JSON: ' + e.message); setOutput(''); }
  }, [input]);

  var copy = useCallback(function () {
    if (!output) return;
    navigator.clipboard.writeText(output).then(function () {
      setCopied(true); setTimeout(function () { setCopied(false); }, 2000);
    });
  }, [output]);

  return (
    <div className="page-prettifier">
      <SEO title="JSON Formatter - Format & Validate JSON Online" description="Free online JSON formatter and validator. Paste your JSON, choose your indent style, and get beautifully formatted output instantly." path="/json-formatter" />
      <div className="container">
        <div className="tool-header">
          <h1>JSON Formatter & Validator</h1>
          <p>Paste raw or messy JSON below, then click Format to clean it up. Works for anyone — no coding knowledge needed.</p>
        </div>
        <AdUnit />
        <div className="toolbar">
          <div className="toolbar-left">
            <label htmlFor="indent-select" className="indent-label">Indent:</label>
            <select id="indent-select" value={indent} onChange={function (e) { setIndent(Number(e.target.value)); }} className="indent-select">
              <option value={2}>2 spaces</option>
              <option value={4}>4 spaces</option>
            </select>
          </div>
          <div className="toolbar-right">
            <button onClick={function () { setInput(SAMPLE); setError(''); }} className="btn btn-secondary" type="button">Sample</button>
            <button onClick={function () { setInput(''); setOutput(''); setError(''); }} className="btn btn-secondary" type="button">Clear</button>
            <button onClick={prettify} className="btn btn-primary" type="button">Format</button>
            <button onClick={minify} className="btn btn-accent" type="button">Minify</button>
          </div>
        </div>
        {error && <div className="error-banner" role="alert"><span className="error-icon" aria-hidden="true">{"⚠"}</span> {error}</div>}
        <div className="editor-grid">
          <div className="editor-pane">
            <div className="pane-header"><span>Input</span><span className="char-count">{input.length} chars</span></div>
            <textarea className="editor-textarea" value={input} onChange={function (e) { setInput(e.target.value); }} placeholder="Paste your JSON here..." spellCheck={false} aria-label="JSON input" />
          </div>
          <div className="editor-pane">
            <div className="pane-header"><span>Output</span><button onClick={copy} className="copy-btn" disabled={!output} type="button" aria-label="Copy output">{copied ? '✓ Copied' : 'Copy'}</button></div>
            <textarea className="editor-textarea output-textarea" value={output} readOnly placeholder="Formatted JSON will appear here..." spellCheck={false} aria-label="JSON output" />
          </div>
        </div>
        <section className="tool-guide">
          <h2>What is JSON?</h2>
          <p>JSON (JavaScript Object Notation) is a simple way to store and share data. You have probably seen it without knowing — it powers most websites, apps, and APIs behind the scenes. If you have ever copied data from a website, exported settings, or worked with an API, chances are it was in JSON format.</p>
          <h2>How to Use This Tool</h2>
          <ol>
            <li>Paste your JSON into the Input box on the left.</li>
            <li>Click "Format" to make it readable with proper spacing.</li>
            <li>Click "Minify" if you need a compact version instead.</li>
            <li>Click "Copy" to grab the result.</li>
          </ol>
          <p>If your JSON has errors, you will see a clear message explaining what went wrong so you can fix it.</p>
        </section>
      </div>
    </div>
  );
}

export default JsonFormatter;
